import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/app/Header";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Check, Sparkles } from "lucide-react";

export default function Pricing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [packages, setPackages] = useState([]);
  const [loadingId, setLoadingId] = useState(null);

  useEffect(() => {
    api.get("/packages").then((r) => setPackages(r.data));
  }, []);

  const buy = async (pkgId) => {
    if (!user) {
      toast.error("Sign in first to purchase");
      nav("/login");
      return;
    }
    setLoadingId(pkgId);
    try {
      const r = await api.post("/checkout/session", {
        package_id: pkgId,
        origin_url: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Checkout failed");
      setLoadingId(null);
    }
  };

  const tiers = [
    {
      id: "free",
      scans: 3,
      amount: 0,
      label: "Free",
      note: "Get started",
      free: true,
      features: ["3 scan on signup", "Full Profit Report", "Marketplace intel"],
    },
    ...packages.map((p) => ({
      ...p,
      note: p.id === "pro" ? "Best value" : p.id === "reseller" ? "For pros" : "Starter",
      highlight: p.id === "pro",
      features: [
        `${p.scans.toLocaleString()} scans`,
        "Scans never expire",
        "All marketplaces",
        "Risk flags & verdicts",
      ],
    })),
  ];

  return (
    <div className="min-h-screen mesh-bg">
      <Header />
      <div className="max-w-6xl mx-auto px-6 py-20">
        <div className="text-center mb-16 max-w-2xl mx-auto">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-emerald-400 mb-3">Pricing</p>
          <h1 className="font-display font-semibold text-4xl sm:text-6xl tracking-tight leading-[1.05]">
            Pay per scan.<br />No subscription.
          </h1>
          <p className="text-zinc-400 mt-5 text-lg">
            Buy a pack once. Scans never expire. Flip on your schedule.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {tiers.map((t) => {
            const highlight = t.highlight;
            const perScan = !t.free && t.amount ? (t.amount / t.scans).toFixed(2) : null;
            return (
              <div
                key={t.id}
                className={`relative rounded-2xl border p-7 flex flex-col ${
                  highlight
                    ? "border-emerald-500/40 bg-gradient-to-b from-emerald-500/[0.08] to-emerald-500/[0.02] shadow-xl shadow-emerald-500/10"
                    : "border-white/8 bg-white/[0.015]"
                }`}
                data-testid={`pricing-card-${t.id}`}
              >
                {highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] font-mono text-[#0a0f0d] bg-emerald-400 rounded-full px-3 py-1">
                    <Sparkles className="w-3 h-3" /> Most Popular
                  </div>
                )}

                <div className="text-xs uppercase tracking-widest text-zinc-500 font-mono">{t.note}</div>

                <div className="mt-5">
                  {t.free ? (
                    <div className="font-display font-semibold text-4xl tracking-tight leading-none">Free</div>
                  ) : (
                    <div className="flex items-baseline gap-1.5">
                      <span className="font-display font-semibold text-2xl text-zinc-400 leading-none">$</span>
                      <span className="font-display font-semibold text-5xl tracking-tight leading-none">
                        {Math.floor(t.amount)}
                      </span>
                      <span className="font-display font-semibold text-2xl text-zinc-400 leading-none">
                        .{String(t.amount).split(".")[1] || "00"}
                      </span>
                    </div>
                  )}
                  <div className="text-sm text-zinc-400 mt-3">
                    {t.scans.toLocaleString()} {t.scans === 1 ? "scan" : "scans"}
                  </div>
                  {perScan && (
                    <div className="text-xs text-zinc-500 mt-1 font-mono">${perScan} per scan</div>
                  )}
                </div>

                <ul className="mt-6 space-y-2.5 flex-1">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-zinc-300">
                      <Check className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => (t.free ? nav(user ? "/dashboard" : "/register") : buy(t.id))}
                  disabled={loadingId === t.id}
                  className={`mt-7 rounded-lg font-medium h-11 ${
                    highlight
                      ? "bg-emerald-500 hover:bg-emerald-600 text-[#0a0f0d]"
                      : "bg-white/5 hover:bg-white/10 text-white"
                  }`}
                  data-testid={`buy-${t.id}`}
                >
                  {loadingId === t.id
                    ? "Loading…"
                    : t.free
                    ? user
                      ? "Start scanning"
                      : "Sign up free"
                    : "Buy pack"}
                </Button>
              </div>
            );
          })}
        </div>

        <p className="text-center text-xs text-zinc-500 mt-12">
          Secured by Stripe · One-time payment · No auto-renewal
        </p>
      </div>
    </div>
  );
}
